/**
 * Subdomain Route Guard
 * Automatically redirects users to the correct dashboard based on subdomain
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getSubdomain, 
  getDefaultRoute, 
  isCorrectSubdomainForRole,
  getSubdomainUrl,
  SubdomainType 
} from '../utils/subdomainRouter';

interface SubdomainGuardProps {
  children: React.ReactNode;
}

export const SubdomainGuard: React.FC<SubdomainGuardProps> = ({ children }) => {
  const { user, userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  
  const userRole = userProfile?.role;

  useEffect(() => {
    if (loading) return;

    const subdomain = getSubdomain();
    
    // If on main domain, allow normal routing
    if (subdomain === 'main') {
      setIsChecking(false);
      return;
    }

    // If not logged in on a subdomain, redirect to login
    if (!user) {
      navigate('/authentication');
      setIsChecking(false);
      return;
    }

    // Check if user is on the correct subdomain for their role
    if (userRole && !isCorrectSubdomainForRole(subdomain, userRole)) {
      // Redirect to correct subdomain
      const correctUrl = getSubdomainUrl(userRole);
      window.location.href = correctUrl;
      return;
    }

    // If user is on correct subdomain, redirect to their dashboard
    const currentPath = window.location.pathname;
    if (currentPath === '/' || currentPath === '') {
      const defaultRoute = getDefaultRoute(subdomain);
      navigate(defaultRoute);
    }

    setIsChecking(false);
  }, [user, userRole, loading, navigate]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * Hook to get current subdomain info
 */
export const useSubdomain = () => {
  const [subdomain, setSubdomain] = useState<SubdomainType>('main');

  useEffect(() => {
    setSubdomain(getSubdomain());
  }, []);

  return {
    subdomain,
    isAdmin: subdomain === 'admin',
    isBrand: subdomain === 'brand',
    isCreator: subdomain === 'creator',
    isMain: subdomain === 'main',
  };
};

export default SubdomainGuard;
